/**
 * Utility functions for generating structured data
 */

/**
 * Creates breadcrumb structured data
 * @param {Array<{name: string, path: string}>} items - Array of breadcrumb items
 * @param {string} siteUrl - Base URL of the site
 * @returns {Object} Structured data for breadcrumbs
 */
export const createBreadcrumbData = (items, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `${siteUrl}${item.path}`
  }))
});

/**
 * Creates article structured data
 * @param {Object} params - Article parameters
 * @param {string} params.title - Article title
 * @param {string} params.description - Article description/excerpt
 * @param {string} params.image - Article image URL
 * @param {string} params.publishDate - Article publish date
 * @param {Object} params.author - Article author object
 * @param {string} params.path - Article path
 * @param {string} params.body - Article body text
 * @param {string} siteUrl - Base URL of the site
 * @returns {Object} Structured data for article
 */
export const createArticleData = ({
  title,
  description,
  image,
  publishDate,
  author,
  path,
  body
}, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "image": image,
  "datePublished": publishDate,
  "author": {
    "@type": "Person",
    "name": author?.name || "OneShift Editorial Team"
  },
  "publisher": {
    "@type": "Organization",
    "name": "OneShift by Carousell",
    "logo": {
      "@type": "ImageObject",
      "url": `${siteUrl}/images/oneshift-logo-og.png`
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${siteUrl}${path}`
  },
  "articleBody": body
});

/**
 * Creates FAQ structured data
 * @param {Array<{question: string, answer: string}>} items - Array of FAQ items
 * @returns {Object} Structured data for FAQs
 */
export const createFAQData = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": items.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
});

/**
 * Creates article list structured data
 * @param {Array<Object>} articles - Array of article objects
 * @param {string} siteUrl - Base URL of the site
 * @returns {Object} Structured data for article list
 */
export const createArticleListData = (articles, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "itemListElement": articles.map((article, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.thumbnail?.asset?.url,
      "url": article.url,
      "datePublished": article.publishDate,
      "author": {
        "@type": "Organization",
        "name": "OneShift by Carousell",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${siteUrl}/images/oneshift-logo-og.png`
        }
      }
    }
  }))
});

/**
 * Creates website structured data
 * @param {Object} params - Website parameters
 * @param {string} params.name - Website name
 * @param {string} params.description - Website description
 * @param {string} params.image - Website image URL
 * @param {string} params.url - Website URL
 * @param {Object} params.publisher - Website publisher object
 * @param {Object} params.potentialAction - Website potential action object
 * @param {string} siteUrl - Base URL of the site
 * @returns {Object} Structured data for website
 */
export const createWebsiteData = ({
  name,
  description,
  image,
  url,
  publisher,
  potentialAction
}, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": name,
  "description": description,
  "url": url || siteUrl,
  "image": image,
  "publisher": publisher,
  "potentialAction": potentialAction
});

/**
 * Creates organization structured data
 * @param {Object} params - Organization parameters
 * @param {string} params.name - Organization name
 * @param {string} params.description - Organization description
 * @param {string} params.logo - Organization logo URL
 * @param {string} params.url - Organization URL
 * @param {Object} params.address - Organization address
 * @param {Array<{name: string, url: string}>} params.sameAs - Social media links
 * @param {string} siteUrl - Base URL of the site
 * @returns {Object} Structured data for organization
 */
export const createOrganizationData = ({
  name,
  description,
  logo,
  url,
  address,
  sameAs = []
}, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": name,
  "description": description,
  "url": url || siteUrl,
  "logo": logo,
  "sameAs": sameAs,
  "address": address || {
    "@type": "PostalAddress",
    "addressCountry": "SG",
    "addressLocality": "Singapore"
  }
});

/**
 * Creates item list structured data (for pages with a single list of items)
 * @param {Object} params - Item list parameters
 * @param {string} params.name - List name
 * @param {string} params.description - List description
 * @param {string} params.url - List URL
 * @param {Array} params.items - Array of items in the list
 * @param {string} params.itemType - Type of items (defaults to "Article")
 * @returns {Object} Structured data for item list
 */
export const createItemListData = ({ name, description, url, items }) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    ...(name && { name }),
    ...(description && { description }),
    ...(url && { url }),
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => {
      if (item["@type"] === "Product") {
        return {
          ...item,
          position: index + 1
        };
      }
      
      // Default to ListItem if no specific type is provided
      return {
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        description: item.description,
        ...(item.url && { url: item.url }),
        ...(item.image && {
          image: {
            "@type": "ImageObject",
            url: item.image
          }
        })
      };
    })
  };
};

/**
 * Creates tool structured data for calculators and other web tools
 * @param {Object} params - Tool parameters
 * @param {string} params.name - Tool name
 * @param {string} params.description - Tool description
 * @param {string} params.url - Tool URL
 * @param {string} params.image - Tool image URL
 * @param {string} params.provider - Tool provider name
 * @param {string} siteUrl - Base URL of the site
 * @returns {Object} Structured data for web tool
 */
export const createToolData = ({
  name,
  description,
  url,
  image,
  provider = "OneShift by Carousell"
}, siteUrl) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": name,
  "description": description,
  "url": url || siteUrl,
  "image": image,
  "applicationCategory": "FinanceApplication",
  "browserRequirements": "Requires JavaScript. Requires HTML5.",
  "softwareVersion": "1.0",
  "operatingSystem": "All",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "SGD"
  },
  "provider": {
    "@type": "Organization",
    "name": provider
  }
});

/**
 * Creates auto dealer structured data without URL
 * @param {Object} params - Auto dealer parameters
 * @param {string} params.name - Dealer name
 * @param {string} params.address - Dealer address
 * @param {string} params.telephone - Dealer phone number
 * @param {Object} params.openingHours - Opening hours object
 * @returns {Object} Structured data for auto dealer
 */
export const createAutoDealerData = ({
  name,
  address,
  telephone,
  openingHours
}) => ({
  "@context": "https://schema.org",
  "@type": "AutoDealer",
  "name": name,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": address,
    "addressCountry": "SG",
    "addressLocality": "Singapore"
  },
  "telephone": telephone,
  "openingHoursSpecification": Object.entries(openingHours || {})
    .filter(([_, hours]) => hours)
    .map(([day, hours]) => {
      const [start, end] = hours.split("-");
      return {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": day.toUpperCase(),
        "opens": start,
        "closes": end
      };
    })
});

/**
 * Creates auto repair structured data without URL
 * @param {Object} params - Auto repair parameters
 * @param {string} params.name - Service center name
 * @param {string} params.address - Service center address
 * @param {string} params.telephone - Service center phone number
 * @param {Object} params.openingHours - Opening hours object
 * @returns {Object} Structured data for auto repair
 */
export const createAutoRepairData = ({
  name,
  address,
  telephone,
  openingHours
}) => ({
  "@context": "https://schema.org",
  "@type": "AutoRepair",
  "name": name,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": address,
    "addressCountry": "SG",
    "addressLocality": "Singapore"
  },
  "telephone": telephone,
  "openingHoursSpecification": Object.entries(openingHours || {})
    .filter(([_, hours]) => hours)
    .map(([day, hours]) => {
      const [start, end] = hours.split("-");
      return {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": day.toUpperCase(),
        "opens": start,
        "closes": end
      };
    })
});

/**
 * Creates collection page structured data for articles
 * @param {Object} params - Collection page parameters
 * @param {string} params.name - Collection name
 * @param {string} params.description - Collection description
 * @param {string} params.url - Collection URL
 * @param {Array} params.items - Array of article items
 * @returns {Object} Structured data for collection page
 */
export const createCollectionPageData = ({
  name,
  description,
  url,
  items
}) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": name,
  "description": description,
  "url": url,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  },
  "mainEntity": items.map(item => ({
    "@type": "Article",
    "headline": item.name,
    "url": item.url,
    "image": item.image,
    "datePublished": item.datePublished,
    "publisher": {
      "@type": "Organization",
      "name": "OneShift by Carousell",
      "logo": {
        "@type": "ImageObject",
        "url": `${url}/images/oneshift-logo-og.png`
      }
    }
  }))
});

/**
 * Creates OneShift auto dealer network structured data
 * @param {Object} params - OneShift auto dealer parameters
 * @param {string} params.name - Network name
 * @param {string} params.description - Network description
 * @param {string} params.url - Network URL
 * @param {Array} params.services - Array of services offered
 * @returns {Object} Structured data for OneShift auto dealer network
 */
export const createOneShiftAutoDealerData = ({
  name = "OneShift by Carousell",
  description = "Network of 500+ trusted car dealers in Singapore offering the best prices for your used car",
  url,
  services = [
    {
      name: "Car Valuation",
      description: "Free car valuation within 24 hours"
    },
    {
      name: "Car Inspection",
      description: "Non-obligatory viewing and inspection"
    }
  ]
}) => ({
  "@context": "https://schema.org",
  "@type": "AutoDealer",
  "name": name,
  "description": description,
  "url": url,
  "image": `${new URL(url).origin}/images/oneshift-logo-og.png`,
  "telephone": "+6565335857",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "5 Toa Payoh West, #01-00, Motorway Building, Singapore 318877",
    "addressLocality": "Toa Payoh",
    "addressRegion": "Singapore",
    "postalCode": "319255",
    "addressCountry": "SG"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "1.3403",
    "longitude": "103.8592"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Singapore"
  },
  "priceRange": "$$",
  "openingHours": "Mo-Su",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Car Selling Services",
    "itemListElement": services.map(service => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": service.name,
        "description": service.description
      }
    }))
  }
}); 